// CRM Constants - Origin CRM Refactor
// These provide defaults but are OVERRIDABLE by admin settings in config/crmSettings

// ============================================================================
// CONTACT TYPES - Contacts can have multiple types
// ============================================================================
export const CONTACT_TYPES = [
  { value: 'borrower', label: 'Borrower', color: 'blue', icon: 'FiUser', description: 'Loan applicant or co-borrower' },
  { value: 'real-estate-agent', label: 'Real Estate Agent', color: 'purple', icon: 'FiHome', description: 'Buyer or listing agent' },
  { value: 'title-agent', label: 'Title Agent', color: 'orange', icon: 'FiFileText', description: 'Title/escrow professional' },
  { value: 'lender', label: 'Lender Contact', color: 'green', icon: 'FiDollarSign', description: 'Lender representative' },
  { value: 'attorney', label: 'Attorney', color: 'red', icon: 'FiBriefcase', description: 'Real estate attorney' },
  { value: 'personal', label: 'Personal Contact', color: 'gray', icon: 'FiHeart', description: 'Sphere of influence' },
  { value: 'vendor', label: 'Vendor', color: 'cyan', icon: 'FiTool', description: 'Other service provider' },
];

export const CONTACT_TYPE_MAP = CONTACT_TYPES.reduce((acc, type) => {
  acc[type.value] = type;
  return acc;
}, {});

// ============================================================================
// CONTACT TAGS - Categorization and status tags
// ============================================================================
export const CONTACT_TAGS = [
  { value: 'past-client', label: 'Past Client', color: 'green' },
  { value: 'hot-lead', label: 'Hot Lead', color: 'red' },
  { value: 'warm-lead', label: 'Warm Lead', color: 'orange' },
  { value: 'cold-lead', label: 'Cold Lead', color: 'blue' },
  { value: 'referral-partner', label: 'Referral Partner', color: 'purple' },
  { value: 'sphere', label: 'Sphere of Influence', color: 'blue' },
  { value: 'vip', label: 'VIP', color: 'yellow' },
  { value: 'niche-market', label: 'Niche Market', color: 'teal' },
];

// ============================================================================
// CONTACT SOURCES - How the contact was acquired
// ============================================================================
export const CONTACT_SOURCES = [
  { value: 'google-contacts', label: 'Google Contacts Sync', color: 'blue', icon: 'FiCloud' },
  { value: 'manual', label: 'Manual Entry', color: 'gray', icon: 'FiEdit' },
  { value: 'lead-webhook', label: 'Lead Form', color: 'purple', icon: 'FiGlobe' },
  { value: 'import', label: 'CSV Import', color: 'orange', icon: 'FiUpload' },
  { value: 'referral', label: 'Referral', color: 'green', icon: 'FiUsers' },
  { value: 'organic', label: 'Organic', color: 'teal', icon: 'FiTrendingUp' },
  { value: 'spend', label: 'Paid Marketing', color: 'red', icon: 'FiDollarSign' },
  { value: 'retr-import', label: 'RETR Import', color: 'teal', icon: 'FiDatabase' },
];

// ============================================================================
// DEAL PARTY ROLES - Roles that can be assigned to contacts on specific deals
// ============================================================================
// Deal Team roles (multi-owner Phase 3).
//
// `internal: true`  → the party is a Firebase USER (crm_deal_participants.user_id);
//                     being on the team shares the deal with them (deal_team grant).
// `internal: false` → the party is a CONTACT (crm_deal_participants.contact_id).
// `variableKey`     → camelCase namespace for template variables
//                     (e.g. {{csc.firstName}}, {{titleCompany.fullName}}).
// Primary owner is implicit (crm_deals.owner_id — no participant row).
export const DEAL_PARTY_ROLES = [
  // --- Internal roles (CRM users) ---
  { value: 'co-owner', label: 'Co-Owner', allowMultiple: true, contactTypesAllowed: [], icon: 'FiUserCheck', internal: true, variableKey: 'coOwner' },
  { value: 'csc', label: 'CSC — Client Success Coordinator', allowMultiple: false, contactTypesAllowed: [], icon: 'FiHeadphones', internal: true, variableKey: 'csc' },
  { value: 'loa', label: 'LOA — Loan Officer Assistant', allowMultiple: false, contactTypesAllowed: [], icon: 'FiUserPlus', internal: true, variableKey: 'loa' },
  // --- External roles (contacts) ---
  { value: 'buyer-agent', label: "Buyer's Agent", allowMultiple: false, contactTypesAllowed: ['real-estate-agent'], icon: 'FiHome', internal: false, variableKey: 'buyersAgent' },
  { value: 'buyer-agent-tc', label: "Buyer's Agent's TC", allowMultiple: false, contactTypesAllowed: ['real-estate-agent', 'vendor', 'personal'], icon: 'FiClipboard', internal: false, variableKey: 'buyersAgentTc' },
  { value: 'listing-agent', label: 'Listing Agent', allowMultiple: false, contactTypesAllowed: ['real-estate-agent'], icon: 'FiHome', internal: false, variableKey: 'listingAgent' },
  { value: 'listing-agent-tc', label: "Listing Agent's TC", allowMultiple: false, contactTypesAllowed: ['real-estate-agent', 'vendor', 'personal'], icon: 'FiClipboard', internal: false, variableKey: 'listingAgentTc' },
  // Keep the title-agent slug — Arive sync and 3k+ existing rows write it
  { value: 'title-agent', label: 'Title Company', allowMultiple: false, contactTypesAllowed: ['title-agent'], icon: 'FiFileText', internal: false, variableKey: 'titleCompany' },
  { value: 'lender-rep', label: 'Lender Representative', allowMultiple: false, contactTypesAllowed: ['lender'], icon: 'FiDollarSign', internal: false, variableKey: 'lenderRep' },
  { value: 'co-borrower', label: 'Co-Borrower', allowMultiple: true, contactTypesAllowed: ['borrower'], icon: 'FiUsers', internal: false, variableKey: 'coBorrower' },
  { value: 'attorney', label: 'Attorney', allowMultiple: false, contactTypesAllowed: ['attorney'], icon: 'FiBriefcase', internal: false, variableKey: 'attorney' },
  { value: 'referral-source', label: 'Referral Source', allowMultiple: false, contactTypesAllowed: ['real-estate-agent', 'personal', 'vendor'], icon: 'FiUserPlus', internal: false, variableKey: 'referralSource' },
  { value: 'appraiser', label: 'Appraiser', allowMultiple: false, contactTypesAllowed: ['vendor'], icon: 'FiClipboard', internal: false, variableKey: 'appraiser' },
  { value: 'inspector', label: 'Home Inspector', allowMultiple: false, contactTypesAllowed: ['vendor'], icon: 'FiSearch', internal: false, variableKey: 'inspector' },
];

// ============================================================================
// NETWORK RELATIONSHIP TYPES - General relationships not tied to specific deals
// ============================================================================
export const NETWORK_RELATIONSHIP_TYPES = [
  { value: 'referral-partner', label: 'Referral Partner', reciprocal: 'referral-partner', color: 'purple', icon: 'FiUsers' },
  { value: 'preferred-vendor', label: 'Preferred Vendor', reciprocal: 'client', color: 'blue', icon: 'FiStar' },
  { value: 'colleague', label: 'Colleague', reciprocal: 'colleague', color: 'cyan', icon: 'FiBriefcase' },
  { value: 'sphere', label: 'Sphere of Influence', reciprocal: 'sphere-member', color: 'green', icon: 'FiCircle' },
  { value: 'mentor', label: 'Mentor', reciprocal: 'mentee', color: 'orange', icon: 'FiAward' },
  { value: 'family', label: 'Family', reciprocal: 'family', color: 'pink', icon: 'FiHeart' },
];

// ============================================================================
// CUSTOM FIELD TYPES - Types of custom fields admins can create
// ============================================================================
export const CUSTOM_FIELD_TYPES = [
  { value: 'text', label: 'Text (Short)', icon: 'FiType' },
  { value: 'textarea', label: 'Text (Long)', icon: 'FiAlignLeft' },
  { value: 'number', label: 'Number', icon: 'FiHash' },
  { value: 'select', label: 'Dropdown (Single)', icon: 'FiList' },
  { value: 'multiselect', label: 'Dropdown (Multiple)', icon: 'FiCheckSquare' },
  { value: 'date', label: 'Date', icon: 'FiCalendar' },
  { value: 'boolean', label: 'Yes/No Toggle', icon: 'FiToggleLeft' },
  { value: 'email', label: 'Email', icon: 'FiMail' },
  { value: 'phone', label: 'Phone', icon: 'FiPhone' },
  { value: 'url', label: 'Website URL', icon: 'FiGlobe' },
];

// ============================================================================
// DEAL STAGES - Aligned 1:1 with Arive LOS as of 2026-05-20
// See migration plan: arive-aligned_stage_migration_53a12ba2.plan.md
// ============================================================================
export const STAGES = [
  // Lead stages (Origin-managed)
  { value: 'lead-new',                  label: 'New Lead',               color: 'blue'   },
  { value: 'lead-contacted',            label: 'Contacted',              color: 'cyan'   },
  { value: 'lead-quoted',               label: 'Quoted',                 color: 'teal'   },
  // Loan stages (1:1 with Arive)
  { value: 'loan-application-intake',   label: 'Application Intake',     color: 'purple' },
  { value: 'loan-qualification',        label: 'Qualification',          color: 'pink'   },
  { value: 'loan-preapproved',          label: 'Pre-Approved',           color: 'orange' },
  { value: 'loan-setup',                label: 'Loan Setup',             color: 'yellow' },
  { value: 'loan-disclosure-sent',      label: 'Disclosure Sent',        color: 'yellow' },
  { value: 'loan-submitted',            label: 'UW Submitted',           color: 'orange' },
  { value: 'loan-approved-conditions',  label: 'Approved w/ Conditions', color: 'orange' },
  { value: 'loan-resubmittal',          label: 'Re-Submittal',           color: 'red'    },
  { value: 'loan-clear-to-close',       label: 'Clear to Close',         color: 'green'  },
  { value: 'loan-docs-out',             label: 'Docs Out',               color: 'green'  },
  { value: 'loan-docs-signed',          label: 'Docs Signed',            color: 'green'  },
  { value: 'loan-funded',               label: 'Funded',                 color: 'green'  },
  { value: 'loan-brokercheck-received', label: 'Broker Check Received',  color: 'green'  },
  { value: 'loan-finalized',            label: 'Finalized',              color: 'green'  },
  // Terminal-negative
  { value: 'deal-lost',                 label: 'Lost',                   color: 'red'    },
  // Legacy stages — DO NOT use for new deals. Kept so 37 historical deals
  // continue to render with correct labels/colors. Marked isLegacy=true so
  // pickers and pipelines can filter them out.
  { value: 'lead-contacted-hot',        label: 'Contacted (Hot)',        color: 'cyan',   isLegacy: true },
  { value: 'lead-qualified',            label: 'Qualified (Legacy)',     color: 'purple', isLegacy: true },
  { value: 'lead-qualified-hot',        label: 'Qualified (Hot)',        color: 'purple', isLegacy: true },
  { value: 'lead-preapproved-legacy',   label: 'Pre-Approved (Lead)',    color: 'orange', isLegacy: true },
  { value: 'lead-preapproved-shopper',  label: 'Active Shopper',         color: 'green',  isLegacy: true },
];

export const STAGE_MAP = STAGES.reduce((acc, stage) => {
  acc[stage.value] = stage;
  return acc;
}, {});

// ============================================================================
// STAGE GROUPINGS - shared constants used everywhere a stage list is needed.
// Backend mirror at functions/CRM/constants/stages.js (must stay in sync).
// ============================================================================

/** Terminal-positive stages — a funded loan in any form. */
export const FUNDED_STAGES = [
  'loan-funded',
  'loan-brokercheck-received',
  'loan-finalized',
];

/** All terminal stages (lost + every funded variant). */
export const CLOSED_STAGES = [...FUNDED_STAGES, 'deal-lost'];

/** Origin lead stages — pre-application. */
export const LEAD_STAGES = ['lead-new', 'lead-contacted', 'lead-quoted'];

/** Loan workflow stages — post-application, pre-funding. */
export const ACTIVE_LOAN_STAGES = [
  'loan-application-intake',
  'loan-qualification',
  'loan-preapproved',
  'loan-setup',
  'loan-disclosure-sent',
  'loan-submitted',
  'loan-approved-conditions',
  'loan-resubmittal',
  'loan-clear-to-close',
  'loan-docs-out',
  'loan-docs-signed',
];

// ----------------------------------------------------------------------------
// Phase A backwards-compatible variants — include deprecated slugs so existing
// data still matches handlers/filters during the migration window. Swap to the
// canonical variants in Phase B (~7 days post-migration).
// ----------------------------------------------------------------------------

export const CLOSED_STAGES_LEGACY = [...CLOSED_STAGES, 'deal-won'];
export const LEAD_STAGES_LEGACY = [
  ...LEAD_STAGES,
  'lead-qualified',
  'lead-qualified-hot',
  'lead-contacted-hot',
];

// ----------------------------------------------------------------------------
// Postgres array literals for Supabase `.not('stage', 'in', ...)` chains.
// ----------------------------------------------------------------------------

export const closedStagesPg = `(${CLOSED_STAGES.map((s) => `"${s}"`).join(',')})`;
export const closedStagesLegacyPg = `(${CLOSED_STAGES_LEGACY.map((s) => `"${s}"`).join(',')})`;

// Disposition options for logging interactions
export const DISPOSITIONS = [
  { value: '', label: 'Select Disposition' },
  { value: 'left-message', label: 'Left Message' },
  { value: 'spoke-future', label: 'Spoke - Future Opportunity' },
  { value: 'spoke-qualified', label: 'Spoke - Qualified' },
  { value: 'spoke-not-interested', label: 'Spoke - Not Interested' },
  { value: 'no-answer', label: 'No Answer' },
  { value: 'wrong-number', label: 'Wrong Number' },
  { value: 'do-not-contact', label: 'Do Not Contact' },
  { value: 'sent-email', label: 'Sent Email' },
  { value: 'sent-quote', label: 'Sent Quote' },
  { value: 'scheduled-meeting', label: 'Scheduled Meeting' },
];

// Next action options
export const NEXT_ACTIONS = [
  { value: '', label: 'Select Next Action' },
  { value: 'follow-up-call', label: 'Follow Up Call' },
  { value: 'send-quote', label: 'Send Quote' },
  { value: 'schedule-meeting', label: 'Schedule Meeting' },
  { value: 'collect-documents', label: 'Collect Documents' },
  { value: 'submit-application', label: 'Submit Application' },
  { value: 'follow-up-email', label: 'Follow Up Email' },
  { value: 'nurture', label: 'Nurture' },
  { value: 'close-deal', label: 'Close Deal' },
];

// Lead sources
export const LEAD_SOURCES = [
  { value: 'google-ads-dscr', label: 'Google Ads - DSCR' },
  { value: 'website-organic', label: 'Website - Organic' },
  { value: 'google', label: 'Google' },
  { value: 'aivo', label: 'AIVO' },
  { value: 'portfolio', label: 'Past Client' },
  { value: 'self-gen', label: 'Self-Generated' },
  { value: 'organic', label: 'Organic' },
  { value: 'spend', label: 'Spend' },
  { value: 'referral', label: 'Referral' },
  { value: 'zillow', label: 'Zillow' },
  { value: 'realtor', label: 'Realtor.com' },
  { value: 'boomtown', label: 'BoomTown' },
  { value: 'myhomeiq', label: 'MyHomeIQ' },
  { value: 'in-house-refi', label: 'In House Refinance' },
  { value: 'arive', label: 'Arive' },
  { value: 'webhook-other', label: 'Webhook - Other' },
  { value: 'other', label: 'Other' },
];

// Product types
export const PRODUCT_TYPES = [
  { value: 'conventional', label: 'Conventional' },
  { value: 'fha', label: 'FHA' },
  { value: 'va', label: 'VA' },
  { value: 'usda', label: 'USDA' },
  { value: 'jumbo', label: 'Jumbo' },
  { value: 'other', label: 'Other' },
];

// Property types
export const PROPERTY_TYPES = [
  { value: 'Single Family', label: 'Single Family' },
  { value: 'Condo', label: 'Condo' },
  { value: 'Townhome', label: 'Townhome' },
  { value: 'Multi-Family', label: 'Multi-Family' },
  { value: 'Manufactured', label: 'Manufactured' },
  { value: 'Land', label: 'Land' },
];

// Loan purposes
export const LOAN_PURPOSES = [
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Rate/Term Refinance', label: 'Rate/Term Refinance' },
  { value: 'Cash Out', label: 'Cash Out' },
];

// Occupancy types - For automation: when deal closes, primary_residence updates contact mailing address
export const OCCUPANCY_TYPES = [
  { value: 'primary_residence', label: 'Primary Residence', description: 'Contact will live here - auto-updates mailing address on close' },
  { value: 'second_home', label: 'Second Home', description: 'Vacation home - does not update mailing address' },
  { value: 'investment', label: 'Investment Property', description: 'Rental or flip - does not update mailing address' },
];

// Tag options
export const TAGS = [
  { value: 'hot', label: 'Hot', color: 'red' },
  { value: 'cold', label: 'Cold', color: 'blue' },
  { value: 'warm', label: 'Warm', color: 'orange' },
  { value: 'quoted', label: 'Quoted', color: 'purple' },
  { value: 'follow-up', label: 'Follow-Up', color: 'yellow' },
  { value: 'callback', label: 'Callback', color: 'teal' },
  { value: 'pre-approval', label: 'Pre-Approval', color: 'green' },
];

// ----------------------------------------------------------------------------
// Nurture routing
// ----------------------------------------------------------------------------
// Maps a deal `source` (= crm_deals.source_medium) to a paired nurture campaign.
// When a user logs an activity with Next Step = "Nurture" on a deal whose source
// matches a route, they are prompted to add `nurtureTag` (the nurture campaign's
// TAG_ADDED trigger) and optionally remove the lead from `mainCampaignTemplateId`.
// To add a new source -> nurture pairing, append one object here.
export const NURTURE_ROUTES = [
  {
    source: 'google-ads-va',
    label: 'VA Lead Nurture',
    nurtureTag: 'va-nurture',
    mainCampaignTemplateId: 'a0000006-0000-0000-0000-000000000001',
    mainCampaignName: 'VA Lead Campaign - AI Generated',
  },
];

// Returns the nurture route for a given deal source, or null when none matches.
export const getNurtureRouteForSource = (source) => {
  if (!source) return null;
  return NURTURE_ROUTES.find((route) => route.source === source) || null;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Stage helpers
export const getStageColor = (stageValue) => {
  const stage = STAGE_MAP[stageValue];
  return stage ? stage.color : 'gray';
};

export const getStageLabel = (stageValue) => {
  const stage = STAGE_MAP[stageValue];
  return stage ? stage.label : stageValue;
};

// Contact type helpers
export const getContactTypeConfig = (typeValue) => {
  return CONTACT_TYPE_MAP[typeValue] || null;
};

export const getContactTypeLabel = (typeValue) => {
  const type = CONTACT_TYPE_MAP[typeValue];
  return type ? type.label : typeValue;
};

export const getContactTypeColor = (typeValue) => {
  const type = CONTACT_TYPE_MAP[typeValue];
  return type ? type.color : 'gray';
};

// Deal party role helpers
export const getDealPartyRoleConfig = (roleValue, settings = null) => {
  const roles = settings?.dealPartyRoles || DEAL_PARTY_ROLES;
  return roles.find(r => r.value === roleValue) || null;
};

// Network relationship helpers
export const getNetworkRelationshipConfig = (typeValue, settings = null) => {
  const types = settings?.networkRelationshipTypes || NETWORK_RELATIONSHIP_TYPES;
  return types.find(t => t.value === typeValue) || null;
};

